import classNames from 'classnames';
import ArrowRightIcon from '@heroicons/react/20/solid/ArrowRightIcon';
import { ThemeBackground, ThemeText } from '../../../context/theme/ThemeConstantsUpdated';

const { mainBackground, borderBackground } = ThemeBackground;
const { mainText, tertiaryText, placeholderTextIntense, secondaryText, secondaryTextHover } = ThemeText;

const SearchIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
    <path
      fillRule="evenodd"
      d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z"
      clipRule="evenodd"
    />
  </svg>
);

const ClearIcon = () => (
  <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
    <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
  </svg>
);

export const RoundedSearchInput = (props: Props) => {
  const {
    value,
    onChange,
    onSubmit,
    onClear,
    placeholder,
    autoFocus,
    className,
    inputClassName,
    altIcon,
    removeShadow,
    disableSubmit,
    outline,
    classOverride,
  } = props;

  const hasBackgroundOverride = classOverride?.background !== undefined;
  const hasBorderOverride = classOverride?.border !== undefined;
  const hasTextOverride = classOverride?.text !== undefined;
  const hasPlaceholderTextOverride = classOverride?.placeholderText !== undefined;

  const handleSubmit = () => {
    if (disableSubmit) {
      return;
    }

    const trimmedValue = value.trim();

    if (!trimmedValue) {
      return;
    }

    onSubmit(trimmedValue);
  };

  const handleReset = () => {
    onClear?.();
  };

  return (
    <div
      className={classNames(
        'relative flex w-full items-center',
        outline && [
          !hasBackgroundOverride && mainBackground,
          !hasBorderOverride && borderBackground,
          'rounded-full border focus-within:border-user-primary-4 focus-within:ring-1 focus-within:ring-user-primary-4 focus-within:ring-offset-0',
          classOverride?.background,
          classOverride?.border,
        ],
        className
      )}
    >
      <input
        type="text"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && !disableSubmit) {
            handleSubmit();
          }
        }}
        className={classNames(
          !hasTextOverride && mainText,
          !hasPlaceholderTextOverride && placeholderTextIntense,
          classOverride?.text,
          classOverride?.placeholderText,
          'block w-full border-0 bg-transparent py-3 pl-5 pr-20 text-base focus:outline-none focus:ring-0',
          removeShadow ? '' : 'shadow-md',
          inputClassName
        )}
        placeholder={placeholder}
        autoFocus={autoFocus}
      />

      <div className="absolute inset-y-0 right-0 flex items-center gap-1 pr-3">
        {Boolean(value) && (
          <button
            onClick={handleReset}
            className={classNames(tertiaryText, 'flex items-center rounded-full p-1.5 hover:bg-user-gray-3')}
            aria-label="Clear search"
            type="button"
          >
            <ClearIcon />
          </button>
        )}
        <button
          onClick={handleSubmit}
          disabled={disableSubmit}
          className={classNames(
            secondaryText,
            secondaryTextHover,
            'flex items-center rounded-full p-1.5 hover:bg-user-gray-3 disabled:cursor-not-allowed disabled:opacity-40'
          )}
          aria-label="Search"
          type="button"
        >
          {altIcon ? <ArrowRightIcon className="h-5 w-5" /> : <SearchIcon />}
        </button>
      </div>
    </div>
  );
};

type Props = {
  value: string;
  onChange: (value: string) => void;
  onSubmit: (value: string) => void;
  onClear?: () => void;
  placeholder?: string;
  autoFocus?: boolean;
  className?: string;
  inputClassName?: string;
  altIcon?: boolean;
  removeShadow?: boolean;
  disableSubmit?: boolean;
  outline?: boolean;
  classOverride?: {
    text?: string;
    placeholderText?: string;
    border?: string;
    background?: string;
  };
};
